/**
 * The question tree (docs/ux/loi.md): one row per node in the letter's own
 * numbering, parts indented; the number is a static label no control
 * touches; the text is edited in place beside the machine text. Missing is
 * never offered for a question: the letter asks it. The standing
 * instructions follow, read-only.
 */

import { cn } from 'cn';
import { ChevronRightIcon } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import type { LoiQuestionDecisionInput, LoiQuestionView, LoiView } from '@/app/types';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { longDate } from '@/lib/format';
import { DecisionMark } from '../casefile/ReviewQueue';
import type { Locked } from './FieldTable';
import { DECISION_WORD, SEP, confidenceText, decisionSentence, draftStamp, questionName } from './describe';

export type QuestionTreeProps = {
  matterRef: string;
  questions: LoiQuestionView[];
  tree: LoiView['tree'];
  standingInstructions: LoiView['standingInstructions'];
  selectedId: string | null;
  locked: Locked;
  offline: boolean;
  busyId: string | null;
  saveFailedId: string | null;
  onSelect: (id: string | null) => void;
  onDecide: (id: string, input: LoiQuestionDecisionInput) => void;
  onShowPage: (page: number, label: string) => void;
  onNext: () => void;
  hasNext: boolean;
  refusedReason: { id: string; reason: string } | null;
};

/** Typed wording or a note survives moving to another row; `against` names the saved decision it was typed over (see FieldDraft), and a decision saved since drops it. */
export type QuestionDraft = { against: string; text: string; choice: Choice | null; note: string; noteOpen: boolean };

const CELL = 'px-3 py-2 align-top text-sm leading-[1.45]';

function countAnswerable(nodes: LoiView['tree']): number {
  return nodes.reduce((n, q) => n + (q.children.length === 0 || q.answerableWithChildren ? 1 : 0) + countAnswerable(q.children), 0);
}

export function QuestionTree(props: QuestionTreeProps) {
  const { matterRef, questions, tree, standingInstructions, selectedId, locked, onSelect } = props;
  const drafts = useRef(new Map<string, QuestionDraft>());
  const decided = questions.filter((q) => (q.item?.decision ?? 'unresolved') !== 'unresolved').length;
  const withParts = questions.filter((q) => q.hasChildren).length;
  const focusId = selectedId && questions.some((q) => q.id === selectedId) ? selectedId : questions[0]?.id ?? null;

  function onKey(e: KeyboardEvent<HTMLButtonElement>, id: string) {
    const index = questions.findIndex((q) => q.id === id);
    const move = (to: number) => { const t = questions[Math.max(0, Math.min(questions.length - 1, to))]; if (t) { e.preventDefault(); onSelect(t.id); requestAnimationFrame(() => document.getElementById(`loi-q-${encodeURIComponent(t.id)}`)?.focus()); } };
    if (e.key === 'ArrowDown') move(index + 1);
    else if (e.key === 'ArrowUp') move(index - 1);
    else if (e.key === 'Home') move(0);
    else if (e.key === 'End') move(questions.length - 1);
    else if (e.key === 'Escape' && selectedId === id) { e.preventDefault(); onSelect(null); }
  }

  return (
    <div data-slot="question-tree" className="flex flex-col gap-4">
      <div className="overflow-hidden rounded-xl border border-beam-line bg-beam-paper">
        <div className="eyebrow flex h-8 items-center gap-2 bg-beam-cream-deep px-3 text-beam-text-muted"><span className="text-foreground">G</span>Questions <span className="tabular font-normal normal-case tracking-normal">{tree.length} {tree.length === 1 ? 'question' : 'questions'}{withParts > 0 ? `, ${withParts} with parts` : ''}{SEP}{countAnswerable(tree)} answer {countAnswerable(tree) === 1 ? 'box' : 'boxes'}{SEP}{decided} of {questions.length} decided</span></div>
        <table className="w-full table-fixed">
          <caption className="sr-only">Questions in the letter's own numbering for {matterRef}</caption>
          <colgroup><col className="w-[64px]" /><col /><col className="w-[104px]" /></colgroup>
          <thead className="bg-beam-cream-deep">
            <tr>
              <th scope="col" className="eyebrow h-8 px-3 text-left text-beam-text-soft">No.</th>
              <th scope="col" className="eyebrow h-8 px-3 text-left text-beam-text-soft">Question (the letter's own numbering)</th>
              <th scope="col" className="eyebrow h-8 px-3 text-left text-beam-text-soft">Decision</th>
            </tr>
          </thead>
          <tbody>
            {questions.length === 0 ? <tr><td colSpan={3} className={cn(CELL, 'text-beam-text-muted')}>The machine read no questions.</td></tr> : null}
            {questions.flatMap((q) => {
              const selected = q.id === selectedId;
              const decision = q.item?.decision ?? 'unresolved';
              const shownText = q.item?.decision === 'corrected' ? q.item.reviewedText : q.text;
              const rows = [
                <tr key={q.id} data-slot="question-row" data-id={q.id} data-decision={decision} aria-current={selected ? 'true' : undefined} onClick={(e) => { if ((e.target as HTMLElement).closest('button')) return; onSelect(selected ? null : q.id); }} className={cn('cursor-pointer border-t border-l-2 border-beam-line hover:bg-beam-cream-deep', selected ? 'border-l-beam-signature bg-beam-cream-deep' : 'border-l-transparent')}>
                  <td className={cn(CELL, 'tabular font-semibold')} style={{ paddingLeft: 12 + q.depth * 24 }}>{q.label}</td>
                  <td className={CELL}>
                    <button type="button" id={`loi-q-${encodeURIComponent(q.id)}`} aria-expanded={selected} aria-controls={`loi-q-editor-${encodeURIComponent(q.id)}`} aria-label={`${questionName(q)}: ${shownText}. ${DECISION_WORD[decision]}.`} tabIndex={q.id === focusId ? 0 : -1} onClick={() => onSelect(selected ? null : q.id)} onKeyDown={(e) => onKey(e, q.id)} className={cn('block w-full text-left outline-none focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:ring-offset-2', selected && 'font-medium')}>
                      {shownText}
                    </button>
                    <span className="tabular block text-xs text-beam-text-muted">{confidenceText(q.confidence)}{SEP}{q.evidence ? `Page ${q.evidence.page}` : 'No page cited'}{q.hasChildren ? `${SEP}Heading; its parts are answered` : ''}{q.answerableWithChildren ? `${SEP}Also answered in its own right` : ''}</span>
                  </td>
                  <td className={CELL}><DecisionMark word={DECISION_WORD[decision]} /></td>
                </tr>,
              ];
              if (selected) {
                rows.push(
                  <tr key={`${q.id}-editor`} className="border-t border-l-2 border-beam-line border-l-beam-signature">
                    <td colSpan={3} id={`loi-q-editor-${encodeURIComponent(q.id)}`} className="p-0">
                      <QuestionEditor
                        question={q}
                        locked={locked}
                        offline={props.offline}
                        busy={props.busyId === q.id}
                        saveFailed={props.saveFailedId === q.id}
                        refusedReason={props.refusedReason?.id === q.id ? props.refusedReason.reason : null}
                        draft={drafts.current.get(q.id)}
                        onDraft={(d) => { if (d) drafts.current.set(q.id, d); else drafts.current.delete(q.id); }}
                        onClose={() => { onSelect(null); requestAnimationFrame(() => document.getElementById(`loi-q-${encodeURIComponent(q.id)}`)?.focus()); }}
                        onDecide={(input) => props.onDecide(q.id, input)}
                        onShowPage={props.onShowPage}
                        onNext={props.onNext}
                        hasNext={props.hasNext}
                      />
                    </td>
                  </tr>,
                );
              }
              return rows;
            })}
          </tbody>
        </table>
      </div>
      <StandingInstructions instructions={standingInstructions} />
    </div>
  );
}

type Choice = 'accepted' | 'corrected' | 'flagged';

export function QuestionEditor({ question: q, locked, offline, busy, saveFailed, refusedReason, draft: storedDraft, onDraft, onClose, onDecide, onShowPage, onNext, hasNext }: {
  question: LoiQuestionView;
  locked: Locked;
  offline: boolean;
  busy: boolean;
  saveFailed: boolean;
  refusedReason: string | null;
  draft: QuestionDraft | undefined;
  onDraft: (draft: QuestionDraft | null) => void;
  onClose: () => void;
  onDecide: (input: LoiQuestionDecisionInput) => void;
  onShowPage: (page: number, label: string) => void;
  onNext: () => void;
  hasNext: boolean;
}) {
  const id = useId();
  const item = q.item;
  const saved = item && item.decision !== 'unresolved' ? item : null;
  const savedChoice = (): Choice | null => (saved && saved.decision !== 'missing' && saved.decision !== 'unresolved' ? saved.decision : null);
  const stamp = draftStamp(item);
  const draft = storedDraft && storedDraft.against === stamp ? storedDraft : undefined;
  const [text, setText] = useState(draft?.text ?? (saved?.decision === 'corrected' ? saved.reviewedText : q.text));
  const [choice, setChoice] = useState<Choice | null>(draft ? draft.choice : savedChoice());
  const [note, setNote] = useState(draft?.note ?? item?.note ?? '');
  const [noteOpen, setNoteOpen] = useState(draft?.noteOpen ?? Boolean(item?.note));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(Boolean(draft));
  const noteRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    // A draft from an earlier visit wins while it was typed over the decision now saved; a decision saved since (here or in another tab) drops it.
    if (draft) { setText(draft.text); setChoice(draft.choice); setNote(draft.note); setNoteOpen(draft.noteOpen); setErrors({}); setDirty(true); return; }
    if (storedDraft) onDraft(null);
    setText(saved?.decision === 'corrected' ? saved.reviewedText : q.text); setChoice(savedChoice()); setNote(item?.note ?? ''); setNoteOpen(Boolean(item?.note)); setErrors({}); setDirty(false);
  }, [q.id, item?.at, q.text]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (dirty) onDraft({ against: stamp, text, choice, note, noteOpen }); }, [dirty, text, choice, note, noteOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  // A failed click-commit (Accept) must not leave the segment claiming a decision that was not saved.
  useEffect(() => { if (saveFailed && dirty && choice === 'accepted') setChoice(savedChoice()); }, [saveFailed]); // eslint-disable-line react-hooks/exhaustive-deps

  const edited = text.trim() !== q.text.trim();
  const effective: Choice | null = edited ? 'corrected' : choice;
  const label = questionName(q);

  if (locked) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <QuestionPair q={q} text={saved?.decision === 'corrected' ? saved.reviewedText : q.text} />
        <p className="text-[13px]">Decisions are locked: the LOI was approved by {locked.by} on {longDate(locked.at)} at {new Date(locked.at).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: false })}.</p>
      </div>
    );
  }

  function commit(input: LoiQuestionDecisionInput) { setErrors({}); setDirty(true); onDecide(input); }

  function onChoice(next: Choice) {
    if (busy || offline) return;
    if (next === 'accepted' && edited) return;
    setChoice(next);
    setDirty(true);
    if (next === 'accepted') commit({ decision: 'accepted', ...(note.trim() ? { note: note.trim() } : {}) });
    if (next === 'flagged') { setNoteOpen(true); requestAnimationFrame(() => noteRef.current?.focus()); }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || offline) return;
    if (effective === 'flagged') {
      if (!note.trim()) { setErrors({ note: 'Say what is ambiguous' }); noteRef.current?.focus(); return; }
      commit({ decision: 'flagged', note: note.trim() });
      return;
    }
    if (effective === 'corrected') {
      const v = text.trim();
      if (!v) { setErrors({ text: "Enter the question's wording" }); document.getElementById(`${id}-text`)?.focus(); return; }
      if (v.length > 2000) { setErrors({ text: 'At most 2000 characters' }); return; }
      if (!edited) { setErrors({ text: "The wording matches the machine's. Choose Accept." }); return; }
      commit({ decision: 'corrected', reviewedText: v, ...(note.trim() ? { note: note.trim() } : {}) });
      return;
    }
    if (!effective) { setErrors({ choice: 'Choose Accept, Change or Flag, or edit the wording.' }); return; }
    commit({ decision: 'accepted', ...(note.trim() ? { note: note.trim() } : {}) });
  }

  const savedInert = saved !== null && !dirty && saved.decision === effective && (effective === 'corrected' || effective === 'flagged');
  const statusText = offline ? 'Paused while offline. Your typed value is kept.' : busy ? 'Saving' : saveFailed && dirty ? 'Not saved: try again.' : saved ? (saved.auto ? decisionSentence(saved.decision, saved.byName ?? saved.by, saved.at, saved.auto) : decisionSentence(saved.decision, saved.byName ?? saved.by, saved.at).replace(/^\w+/, 'Saved')) : 'Not saved yet';

  return (
    <form data-slot="question-editor" aria-labelledby={`${id}-legend`} noValidate onSubmit={submit} onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); (e.currentTarget as HTMLFormElement).requestSubmit(); } else if (e.key === 'Escape' && !e.defaultPrevented) { e.preventDefault(); onClose(); } }} className="flex flex-col gap-4 p-4">
      {item?.decision === 'flagged' && item.note ? <p role="note" className="rounded-lg border border-l-[3px] border-beam-line border-l-beam-warning bg-beam-warning-bg px-3 py-2 text-[13px] text-beam-warning">Flagged{item.byName ?? item.by ? ` by ${item.byName ?? item.by}` : ''}: <q>{item.note}</q></p> : null}
      <div className="grid gap-6 md:grid-cols-2">
        <div className="flex flex-col gap-1">
          <span className="eyebrow text-beam-text-muted">Machine text</span>
          <p className="text-sm leading-[1.45]">{q.text}</p>
          <p className="text-[13px] text-beam-text-muted">Confidence {confidenceText(q.confidence)}.</p>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-text`} className="eyebrow text-beam-text-muted">Your text (edited in place)</Label>
          <Textarea id={`${id}-text`} rows={Math.min(8, Math.max(2, Math.ceil(text.length / 70)))} maxLength={2000} value={text} onChange={(e) => { setText(e.target.value); setDirty(true); }} aria-invalid={errors.text ? true : undefined} aria-describedby={errors.text ? `${id}-text-error` : undefined} className="text-sm" />
          {text.length >= 1800 ? <span className="tabular text-xs text-beam-text-muted">{text.length} of 2000</span> : null}
          {edited ? <button type="button" className="self-start text-[13px] text-beam-signature underline-offset-4 hover:underline" onClick={() => { setText(q.text); setDirty(true); }}>Restore machine text</button> : null}
          {errors.text ? <p id={`${id}-text-error`} role="alert" className="text-[13px] text-beam-error">{errors.text}</p> : null}
        </div>
      </div>
      {q.evidence ? <p className="text-[13px]">Evidence, page {q.evidence.page}: <q className="text-beam-text-soft">{q.evidence.quote}</q> <button type="button" className="font-medium text-beam-signature underline-offset-4 hover:underline" aria-label={`Show page ${q.evidence.page} of the letter, the evidence cited for ${label}`} onClick={() => onShowPage(q.evidence!.page, label)}>Show page {q.evidence.page}</button></p> : null}
      <p className="text-xs text-beam-text-muted">{label}: the number is the letter's own and is not changed.</p>

      <div className="flex flex-col gap-2">
        <span id={`${id}-legend`} className="eyebrow text-beam-text-muted">Your decision</span>
        <ToggleGroup type="single" value={effective ?? ''} onValueChange={(v) => { if (v && v !== 'missing') onChoice(v as Choice); }} aria-label={`Your decision for ${label}`} aria-describedby={`${id}-desc`} className="justify-start rounded-lg bg-beam-cream-deep p-1">
          <ToggleGroupItem value="accepted" aria-disabled={edited ? 'true' : undefined} aria-describedby={edited ? `${id}-accept-note` : undefined} onClick={(e) => { if (edited) { e.preventDefault(); e.stopPropagation(); } }} className={cn('h-9 px-3 text-sm data-[state=on]:bg-beam-paper data-[state=on]:text-foreground data-[state=on]:shadow-beam-1', edited && 'cursor-default text-beam-text-muted')}>Accept</ToggleGroupItem>
          <ToggleGroupItem value="corrected" className="h-9 px-3 text-sm data-[state=on]:bg-beam-paper data-[state=on]:text-foreground data-[state=on]:shadow-beam-1">Change</ToggleGroupItem>
          <ToggleGroupItem value="missing" aria-disabled="true" aria-describedby={`${id}-missing-note`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} className="h-9 cursor-default px-3 text-sm text-beam-text-muted">Missing</ToggleGroupItem>
          <ToggleGroupItem value="flagged" className="h-9 px-3 text-sm data-[state=on]:bg-beam-paper data-[state=on]:text-foreground data-[state=on]:shadow-beam-1">Flag</ToggleGroupItem>
        </ToggleGroup>
        <p id={`${id}-desc`} className="text-xs text-beam-text-muted">{effective === 'accepted' ? "The machine's wording is right as read." : effective === 'corrected' ? 'Save the wording as the letter has it.' : effective === 'flagged' ? 'Ambiguous; someone else decides. Blocks approval until re-decided.' : 'Choose Accept, Change or Flag, or edit the wording.'}</p>
        <p id={`${id}-missing-note`} className="text-xs text-beam-text-muted">A question the letter asks cannot be missing: correct its wording or flag it.</p>
        {edited ? <p id={`${id}-accept-note`} className="text-xs text-beam-text-muted">Your text differs from the machine's. Save the change, or restore the machine text.</p> : null}
        {errors.choice ? <p role="alert" className="text-[13px] text-beam-error">{errors.choice}</p> : null}
        {refusedReason ? <p role="alert" className="text-[13px] text-beam-error">Refused: {refusedReason}.</p> : null}
      </div>

      {noteOpen || effective === 'flagged' ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-note`}>{effective === 'flagged' ? 'Note (required to flag)' : 'Note (optional)'}</Label>
          <Textarea ref={noteRef} id={`${id}-note`} rows={3} maxLength={500} value={note} onChange={(e) => { setNote(e.target.value); setDirty(true); }} aria-invalid={errors.note ? true : undefined} aria-describedby={errors.note ? `${id}-note-error` : undefined} className="max-w-[480px]" />
          {errors.note ? <p id={`${id}-note-error`} role="alert" className="text-[13px] text-beam-error">{errors.note}</p> : null}
        </div>
      ) : (
        <Collapsible open={false} onOpenChange={() => setNoteOpen(true)}>
          <CollapsibleTrigger className="flex min-h-8 items-center gap-1 text-[13px] text-beam-text-muted hover:text-foreground"><ChevronRightIcon aria-hidden="true" className="size-4" /> Add a note</CollapsibleTrigger>
          <CollapsibleContent />
        </Collapsible>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {effective === 'corrected' || effective === 'flagged' ? (
          <Button type="submit" size="lg" variant={savedInert ? 'outline' : 'default'} className={cn('h-9', savedInert && 'cursor-default text-beam-text-muted hover:bg-beam-paper')} disabled={offline} aria-disabled={busy || savedInert ? 'true' : undefined} aria-describedby={`${id}-status`}>{savedInert && !busy ? 'Saved' : busy ? (effective === 'flagged' ? 'Flagging' : 'Saving') : effective === 'flagged' ? 'Flag question' : 'Save change'}</Button>
        ) : null}
        <span id={`${id}-status`} className="text-xs text-beam-text-muted">{statusText}</span>
        {hasNext ? <Button type="button" variant="ghost" size="default" className="ml-auto h-8" onClick={onNext}>Next unresolved</Button> : null}
      </div>
    </form>
  );
}

function QuestionPair({ q, text }: { q: LoiQuestionView; text: string }) {
  return (
    <div data-slot="machine-human" className="grid gap-6 md:grid-cols-2">
      <div className="flex flex-col gap-1"><span className="eyebrow text-beam-text-muted">Machine text</span><p className="text-sm leading-[1.45]">{q.text}</p></div>
      <div className="flex flex-col gap-1"><span className="eyebrow text-beam-text-muted">Your text</span><p className="text-sm leading-[1.45]">{text}</p>{q.item && q.item.decision !== 'unresolved' ? <p className="text-[13px] text-beam-text-muted">{decisionSentence(q.item.decision, q.item.byName ?? q.item.by, q.item.at, q.item.auto)}</p> : null}</div>
    </div>
  );
}

export function StandingInstructions({ instructions }: { instructions: LoiView['standingInstructions'] }) {
  return (
    <section aria-label="Standing instructions" className="rounded-xl border border-beam-line bg-beam-paper p-4">
      <h3 className="eyebrow mb-2 text-beam-text-muted">Standing instructions <span className="tabular">{instructions.length}</span></h3>
      {instructions.length === 0 ? <p className="text-[13px] text-beam-text-muted">The machine read no standing instructions.</p> : (
        <ul className="flex flex-col divide-y divide-beam-line">
          {instructions.map((s, i) => (
            <li key={`${i}-${s.sourcePage ?? 0}`} className="flex min-h-11 flex-wrap items-center justify-between gap-3 py-2 text-sm"><span>{s.text}</span>{s.sourcePage ? <span className="text-xs text-beam-text-muted">Page {s.sourcePage}</span> : null}</li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-[13px] text-beam-text-muted">Carried into the form as read. If one is wrong, flag the question it bears on and say so in the note.</p>
    </section>
  );
}
